import { Inject, Injectable } from '@angular/core';
import { DocumentFeedbackModel } from '../components/document-feedback/document-feedback.model';
import { catchError, map, Observable, throwError } from 'rxjs';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ENDPOINTS,
    Equipment,
    IncidentPriority,
    IncidentType,
    NotificationIdentifier,
    NotificationInterface,
    NumberUtilities,
    SharpTicketAcceptedResponse,
    UserInfo,
    UserMasterInstanceToken,
    UserMasterInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import moment from 'moment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NotificationInstanceToken } from '../core/interfaces/notification.interface';
import { DocumentFeedbackSharpPayload, DocumentSharpServiceNames } from '../types/document-feedback';
import { EnvironmentService } from '@evorait/pace-environment';

@Injectable({
    providedIn: 'root',
})
export class DocumentFeedbackService {
    public constructor(
        @Inject(NotificationInstanceToken) private readonly notificationService: NotificationInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
        private readonly httpClient: HttpClient,
        private readonly environmentService: EnvironmentService,
    ) {}

    public createSharpIncident(documentFeedbackModel: DocumentFeedbackModel): Observable<void> {
        const body = this.createPayload(documentFeedbackModel);
        const options = {
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        };

        return this.sendPostRequest(
            `${this.environmentService.apiBase}${ENDPOINTS.SharpTicketUri}`,
            body,
            options,
        ).pipe(
            map((response: SharpTicketAcceptedResponse) => {
                if (!response?.accepted) {
                    throw new Error(`Sharp incident creation was not accepted: ${JSON.stringify(response)}`);
                }
                this.log.info(`DocumentFeedbackService: createSharpIncident: SHARP ticket creation queued.`);
            }),
            catchError((error) => {
                this.log.error(
                    `DocumentFeedbackService: createSharpIncident: Error: ${JSON.stringify(
                        error,
                        Object.getOwnPropertyNames(error),
                    )}`,
                );
                return throwError(() => new Error(error));
            }),
        );
    }

    private createPayload(documentFeedbackModal: DocumentFeedbackModel): DocumentFeedbackSharpPayload {
        const externalId = this.createExternalId(documentFeedbackModal.getGID());
        const priority = IncidentPriority.NOT_BLOCKED;
        const shortDescription = `Feedback for Form : ${documentFeedbackModal.form_id.value}`;
        const detailedDescription = this.getDocumentFeedbackDetailedDescription(documentFeedbackModal);
        const incidentType = <IncidentType>documentFeedbackModal.category.selectedEntry.value;
        const requester = new UserInfo(documentFeedbackModal.getGID(), '');

        return new DocumentFeedbackSharpPayload(
            externalId,
            this.getSharpServiceText(documentFeedbackModal.modality),
            priority,
            shortDescription,
            detailedDescription,
            incidentType,
            requester,
            requester,
            [],
        );
    }

    private getDocumentFeedbackDetailedDescription(documentFeedbackModal: DocumentFeedbackModel): string {
        const equipment = this.getMainEquipment(documentFeedbackModal.notification);
        return `
              Material number: ${equipment.matnr}

              Serial Number: ${equipment.sernr}

              Product Key: ${documentFeedbackModal.productKey}

              Docytpe Key: ${documentFeedbackModal.doctypeKey}

              Variant: ${documentFeedbackModal.variant}

              Version: ${documentFeedbackModal.version}

              Language: ${documentFeedbackModal.language}

              Modality: ${documentFeedbackModal.modality}

              Defaultinfoclass: ${documentFeedbackModal.defaultInfoclass}

              Description: ${documentFeedbackModal.feedback_description.value}

              Section number: ${documentFeedbackModal.section_number.value}
          `;
    }

    private createExternalId(gid: string): string {
        return `${gid}-${this.userMasterService.getSharpServiceText()}-${moment.now()}-${NumberUtilities.getRandomNumberBetweenMinAndMax()}`;
    }

    private getMainEquipment(notificationIdentifier: NotificationIdentifier): Equipment {
        const notification = this.notificationService.getNotificationStateByIdentifier(notificationIdentifier);
        if (typeof notification === 'undefined') {
            throw new Error(`DocumentFeedbackService : No notification available for document feedback`);
        }
        const equipment = this.notificationService.getMainEquipmentFromNotification(notification);
        if (typeof equipment === 'undefined') {
            throw new Error(
                `DocumentFeedbackService : No mainEquipment available on notification for document feedback`,
            );
        }
        return equipment;
    }

    private sendPostRequest(
        url: string,
        body: string | DocumentFeedbackSharpPayload,
        options: {
            headers?:
                | HttpHeaders
                | {
                      [header: string]: string | string[];
                  };
        },
    ): Observable<SharpTicketAcceptedResponse> {
        return this.httpClient.post<SharpTicketAcceptedResponse>(url, body, options);
    }

    private getSharpServiceText(key: string): DocumentSharpServiceNames {
        return (
            DocumentSharpServiceNames[key as keyof typeof DocumentSharpServiceNames] || DocumentSharpServiceNames.OTHER
        );
    }
}
